import type { LandingPageContent } from './types'

export const METHODIK_PREISBERECHNUNG: LandingPageContent = {
  slug: 'methodik-preisberechnung',
  lang: 'de',
  topicId: 'methodik-preisberechnung',
  title: 'Wie berechnet Roofora den Preis? Methodik & Datenbasis',
  metaDescription:
    'Woher kommt der Durchschnittspreis im PV-Kostenrechner? Datenbasis, Konfidenzstufen und ' +
    'Aktualisierungsrhythmus transparent erklärt - keine erfundenen Zahlen, keine Bewertungen.',
  keywords: [
    'PV-Kostenrechner Methodik',
    'Wie berechnet Roofora den Preis',
    'Photovoltaik Preisschätzung Datenbasis',
    'Roofora Durchschnittspreis Berechnung',
    'Photovoltaik Ausrichtung Ertrag',
    'Teilverschattung Ertragsverlust',
    'Dachausrichtung Photovoltaik',
  ],
  eyebrow: 'Transparenz',
  h1: 'Wie berechnet Roofora den Preis?',
  intro:
    'Der Durchschnittspreis im PV-Kostenrechner ist keine feste Tabelle und keine Schätzung von ' +
    'Roofora selbst, sondern wird bei jeder Anfrage frisch aus tatsächlich auf der Plattform ' +
    'abgegebenen Handwerker-Angeboten berechnet. Diese Seite erklärt, welche Daten einfließen, ' +
    'wie die Berechnung mit wachsender oder noch kleiner Datenmenge umgeht, und was sie bewusst ' +
    'nicht tut.',
  sections: [
    { type: 'heading', text: 'Datenbasis: echte Angebote, keine erfundenen Zahlen' },
    {
      type: 'paragraph',
      text:
        'Jeder Datenpunkt stammt aus einem echten, auf Roofora abgegebenen Handwerker-Angebot für ' +
        'eine Komplettanlage (Photovoltaik, optional mit Batteriespeicher). Es gibt keine ' +
        'Mindestangebotszahl, ab der die Funktion erst "freigeschaltet" wird, und keine ' +
        'aufgerundeten oder geschätzten Platzhalterwerte - die Datenmenge wächst mit jedem neuen ' +
        'Angebot, ganz gleich wie klein sie an einem bestimmten Tag gerade ist.',
    },
    { type: 'heading', text: 'Vier Konfidenzstufen statt einer einzigen Zahl' },
    {
      type: 'list',
      items: [
        'Regionaler Durchschnitt: wenn genug vergleichbare Angebote aus Ihrer Region (grobe PLZ-Region) vorliegen, wird ausschließlich mit diesen gerechnet.',
        'Bundesweiter Durchschnitt: reichen die regionalen Angebote nicht aus, aber es gibt genug vergleichbare Angebote aus ganz Deutschland, wird stattdessen dieser Durchschnitt gezeigt - deutlich als "bundesweit" gekennzeichnet.',
        'Grobe Schätzung: reicht selbst der bundesweite Vergleich für Ihre genaue Anlagengröße/Speicherkombination nicht aus, wird eine gröbere Schätzung aus dem Preis pro kWp bzw. Speicher-kWh über alle bisherigen Komplettsystem-Angebote hochgerechnet - deutlich als weniger genau gekennzeichnet.',
        'Zu wenig Daten: reicht auch das nicht, wird ehrlich "zu wenig Daten" angezeigt statt eine Zahl zu erfinden. Eine kostenlose, unverbindliche Anfrage bleibt trotzdem möglich.',
      ],
    },
    {
      type: 'paragraph',
      text:
        'Diese Reihenfolge wird bei jeder Berechnung neu durchlaufen und niemals stillschweigend ' +
        'vermischt - Sie sehen also immer, auf welcher der vier Stufen ein Ergebnis beruht, nicht ' +
        'nur eine nackte Zahl ohne Einordnung.',
    },
    { type: 'heading', text: 'Aktualisierung' },
    {
      type: 'paragraph',
      text:
        'Es gibt keinen separaten Aktualisierungs-Rhythmus - jede Berechnung greift live auf den ' +
        'aktuellen Stand aller bislang abgegebenen Angebote zu. Je mehr Handwerksbetriebe über ' +
        'Roofora Angebote abgeben, desto häufiger greift die genauere regionale Stufe statt der ' +
        'bundesweiten oder groben Schätzung.',
    },
    { type: 'heading', text: 'Was diese Berechnung bewusst nicht tut' },
    {
      type: 'list',
      items: [
        'Keine erfundenen oder aufgerundeten Bewertungen/Sternebewertungen - solange es keine echten, offengelegten Nutzerbewertungen gibt, wird auch keine angezeigt.',
        'Kein verbindliches Angebot: die Zahl ist eine Schätzung basierend auf bisherigen Angeboten, kein Festpreis für Ihr konkretes Dach.',
        'Keine Weitergabe Ihrer Postleitzahl oder sonstiger Eingaben an Dritte - die Berechnung selbst speichert nichts.',
      ],
    },
    { type: 'heading', text: 'Dachausrichtung: wie der kostenlose PV-Rechner den Ertrag einschätzt' },
    {
      type: 'paragraph',
      text:
        'Neben dem Preis berechnet auch der kostenlose PV-Rechner (Anlagengrößen-Empfehlung) nach ' +
        'demselben Transparenzprinzip: keine geschätzten Platzhalterwerte, sondern eine offen ' +
        'einsehbare Rechengrundlage. Für die Dachausrichtung verwendet er einen festen Ertragsfaktor ' +
        'je Ausrichtung, mit Süd als Referenzwert (100 %):',
    },
    {
      type: 'list',
      items: [
        'Süd: 100 % (Referenzwert)',
        'Südost / Südwest: 95 %',
        'Ost / West: 85 %',
        'Flachdach: 90 %',
        'Nordost / Nordwest: 70 %',
        'Nord: 60 %',
      ],
    },
    {
      type: 'paragraph',
      text:
        'Diese Werte fließen zusammen mit standortbasierten Einstrahlungsdaten (PVGIS) in die ' +
        'empfohlene Anlagengröße ein. Haben Sie mehrere unterschiedlich ausgerichtete Dachflächen ' +
        '(z. B. ein Ost-West-Dach), geben Sie einfach jede Fläche einzeln mit ihrer eigenen ' +
        'Ausrichtung an - der Rechner berücksichtigt jede Fläche separat.',
    },
    { type: 'heading', text: 'Teilverschattung: warum unsere Schätzung sie nicht einpreist' },
    {
      type: 'paragraph',
      text:
        'Teilverschattung (z. B. durch Bäume, Nachbargebäude oder Schornsteine) kann den Jahresertrag ' +
        'überproportional stark senken - externen Quellen zufolge können bereits 10 % Verschattung ' +
        'einer Dachfläche den Jahresertrag um bis zu 15 % reduzieren. Ehrlich gesagt: Der kostenlose ' +
        'PV-Rechner modelliert Verschattung nicht mit, seine Schätzung basiert ausschließlich auf ' +
        'Ausrichtung, Neigung und Standort-Einstrahlungsdaten. Die tatsächliche Verschattungsprüfung ' +
        'übernimmt real der anfragende Handwerksbetrieb anhand Ihrer Dachbilder bzw. eines ' +
        'Vor-Ort-Termins - das fließt dann in sein individuelles Angebot ein, nicht in die grobe ' +
        'Vorab-Schätzung. Haben Sie mehrere unterschiedlich ausgerichtete oder teilverschattete ' +
        'Dachflächen, plant der Handwerksbetrieb im Angebot auch die passende elektrische Aufteilung ' +
        '(z. B. auf mehrere Wechselrichter-Eingänge) - das ist Teil der professionellen Planung, kein ' +
        'Punkt, den Sie selbst im Vorfeld festlegen müssen.',
    },
  ],
  faq: [
    {
      q: 'Warum sehe ich manchmal "bundesweiter Durchschnitt" statt eines regionalen Preises?',
      a: 'Weil für Ihre Region noch nicht genug vergleichbare Angebote vorliegen. Sobald mehr Handwerksbetriebe aus Ihrer Region Angebote abgeben, wechselt die Berechnung automatisch auf den genaueren regionalen Durchschnitt.',
    },
    {
      q: 'Ist die angezeigte Zahl ein verbindliches Angebot?',
      a: 'Nein. Es ist eine Schätzung auf Basis bisheriger Roofora-Angebote, keine verbindliche Preisangabe für Ihr konkretes Dach. Ein verbindliches Angebot bekommen Sie erst von einem Handwerksbetrieb nach einer echten Anfrage.',
    },
    {
      q: 'Warum gibt es keine Sternebewertungen zum Rechner selbst?',
      a: 'Weil wir keine Bewertungen erfinden. Sobald es echte, offengelegte Nutzerbewertungen gibt, werden diese transparent angezeigt - nicht vorher.',
    },
    {
      q: 'Wird Verschattung meines Dachs bei der automatischen Schätzung berücksichtigt?',
      a: 'Nein - die automatische Schätzung im PV-Rechner berücksichtigt nur Ausrichtung, Neigung und Standort, keine individuelle Verschattung. Die reale Prüfung anhand Ihrer Dachbilder übernimmt der anfragende Handwerksbetrieb im Rahmen seines Angebots.',
    },
  ],
  primaryCta: { label: 'Jetzt PV-Kostenrechner ausprobieren', action: 'link', href: '/pv-kostenrechner' },
  secondaryCta: { label: 'Direkt Anfrage erstellen', action: 'customer' },
  relatedLinks: [
    { label: 'PV-Kostenrechner: Durchschnittspreis für Ihre Anlage berechnen', href: '/pv-kostenrechner' },
    { label: 'Was kostet eine Solaranlage? Preise & Einflussfaktoren im Überblick', href: '/solaranlage-kosten' },
    { label: 'Kostenlos die passende Anlagengröße berechnen', href: '/pv-rechner' },
    { label: 'Photovoltaik Schritt für Schritt planen', href: '/photovoltaik-planen' },
  ],
}
